import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import NotificationCenter from '../notification/Notification.jsx';
import { notifyAuthUserChanged } from '../../utils/authEvents';
import '../home/Home.css';

/* ── Liens de navigation ─────────────────────────────────────────── */
const NAV_LINKS = [
  { label: 'Accueil',   to: '/',          type: 'route'  },
  { label: 'Recherche', to: '/search',    type: 'route'  },
  { label: 'À propos',  to: '/about',     type: 'route'  },
  { label: 'Contact',   to: '/contact',   type: 'route'  },
];

function getStoredAuthUser() {
  try {
    const raw = window.localStorage.getItem('exp-auth-user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/*
 * Navbar — composant partagé pour toutes les pages.
 *
 * Props :
 *   minimal (bool) — mode réduit pour Login / Signup :
 *     affiche seulement le logo + bouton "Accueil", sans liens ni profil.
 */
function Navbar({ minimal = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen]       = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [authUser, setAuthUser]     = useState(() => getStoredAuthUser());

  /* ── Sync auth depuis localStorage ── */
  useEffect(() => {
    const sync = () => setAuthUser(getStoredAuthUser());
    window.addEventListener('storage', sync);
    window.addEventListener('auth-user-changed', sync);
    sync();
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('auth-user-changed', sync);
    };
  }, []);

  /* ── Fermer dropdown profil en cliquant en dehors ── */
  useEffect(() => {
    if (!profileOpen) return;
    const onOut = (e) => { if (!e.target.closest('.vx-profile-dropdown')) setProfileOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setProfileOpen(false); };
    window.addEventListener('mousedown', onOut);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', onOut);
      window.removeEventListener('keydown', onEsc);
    };
  }, [profileOpen]);

  /* ── Bloquer le scroll body quand le menu mobile est ouvert ── */
  useEffect(() => {
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [navOpen]);

  const closeMenu = useCallback(() => {
    setNavOpen(false);
    setProfileOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    window.localStorage.removeItem('exp-auth-user');
    window.localStorage.removeItem('exp-user-profile');
    notifyAuthUserChanged();
    setAuthUser(null);
    navigate('/');
  }, [navigate]);

  /* ── Computed ── */
  const displayName = authUser
    ? `${authUser.firstName || authUser.prenom || ''} ${authUser.lastName || authUser.nom || ''}`.trim() || authUser.email
    : '';
  const initials = authUser
    ? `${(authUser.firstName || authUser.prenom || '').charAt(0)}${(authUser.lastName || authUser.nom || '').charAt(0)}`.toUpperCase()
    : '';
  const avatarUrl   = authUser?.profilePhotoUrl || '';
  const isAdmin     = authUser?.role === 'admin' || authUser?.role === 'agency';
  const dashPath    = authUser?.role === 'admin' ? '/admin' : '/agence';

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to.replace('/#', '/'));
  };

  /* ═══════════════════════ RENDER ═══════════════════════════════ */
  return (
    <>
      <header className="vx-nav-wrap">
        <div className="vx-shell">
          <nav className="vx-nav" aria-label="Navigation principale">

            {/* ── Brand ── */}
            <Link to="/" className="vx-brand" onClick={closeMenu}>
              <div className="vx-brand-mark">
                <img
                  src="/assets/images/logo.png"
                  alt=""
                  aria-hidden="true"
                  className="vx-brand-logo"
                />
              </div>
              <span className="vx-brand-name">UltimateXDrive</span>
            </Link>

            {/* ── Links (desktop) ── */}
            {!minimal && (
              <ul className="vx-nav-links">
                {NAV_LINKS.map((l) => (
                  <li key={l.label}>
                    {l.type === 'route' ? (
                      <Link
                        to={l.to}
                        className={isActive(l.to) ? 'is-current' : ''}
                        onClick={closeMenu}
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <a href={l.to} onClick={closeMenu}>{l.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* ── CTA / Profile ── */}
            <div className="vx-nav-cta">
              {minimal ? (
                <Link to="/" className="vx-btn vx-btn-ghost" onClick={closeMenu}>
                  ← Accueil
                </Link>
              ) : authUser ? (
                <>
                  <NotificationCenter authUser={authUser} onNavigate={closeMenu} />
                  <div className={`vx-profile-dropdown${profileOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className={`vx-profile-trigger${isAdmin ? ' is-admin' : ''}`}
                      onClick={() => setProfileOpen((p) => !p)}
                      aria-expanded={profileOpen}
                      aria-haspopup="menu"
                    >
                      <span className="vx-user-avatar">
                        {avatarUrl
                          ? <img src={avatarUrl} alt="" aria-hidden="true" />
                          : initials || 'U'}
                      </span>
                      <span className="vx-user-name">{displayName}</span>
                      <FiChevronDown size={14} className="vx-profile-chevron" aria-hidden="true" />
                    </button>
                    <div className="vx-profile-dropdown-menu" role="menu" aria-label="Menu profil">
                      <Link to="/profile" className="vx-profile-dropdown-item" onClick={closeMenu} role="menuitem">
                        Profil
                      </Link>
                      {isAdmin && (
                        <Link to={dashPath} className="vx-profile-dropdown-item" onClick={closeMenu} role="menuitem">
                          Dashboard
                        </Link>
                      )}
                      <button
                        type="button"
                        className="vx-profile-dropdown-item"
                        onClick={() => { handleLogout(); closeMenu(); }}
                        role="menuitem"
                      >
                        Déconnexion
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login"  className="vx-btn vx-btn-ghost" onClick={closeMenu}>Connexion</Link>
                  <Link to="/signup" className="vx-btn vx-btn-solid" onClick={closeMenu}>S'inscrire →</Link>
                </>
              )}
            </div>

            {/* ── Hamburger ── */}
            {!minimal && (
              <button
                type="button"
                className={`vx-menu-btn${navOpen ? ' is-open' : ''}`}
                onClick={() => setNavOpen((p) => !p)}
                aria-label={navOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={navOpen}
              >
                <span className="vx-menu-btn-bar" />
                <span className="vx-menu-btn-bar" />
                <span className="vx-menu-btn-bar" />
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* ── Mobile drawer ── rendu EN DEHORS du header pour éviter
           le piège backdrop-filter qui casse position:fixed ── */}
      {!minimal && (
        <div className={`vx-mobile-nav${navOpen ? ' is-open' : ''}`} aria-hidden={!navOpen}>
          <nav className="vx-mobile-nav-links">
            {NAV_LINKS.map((l) =>
              l.type === 'route' ? (
                <Link
                  key={l.label}
                  to={l.to}
                  className={`vx-mobile-nav-link${isActive(l.to) ? ' is-current' : ''}`}
                  onClick={closeMenu}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.to}
                  className="vx-mobile-nav-link"
                  onClick={closeMenu}
                >
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="vx-mobile-nav-auth">
            {authUser ? (
              <>
                <div className="vx-mobile-nav-user">
                  <span className="vx-user-avatar" style={{ width: 40, height: 40, fontSize: '0.9rem' }}>
                    {avatarUrl
                      ? <img src={avatarUrl} alt="" aria-hidden="true" />
                      : initials || 'U'}
                  </span>
                  <div>
                    <strong>{displayName}</strong>
                    <span>{authUser.email}</span>
                  </div>
                </div>
                <Link to="/profile" className="vx-btn vx-btn-ghost vx-nav-full" onClick={closeMenu}>
                  Mon profil
                </Link>
                {isAdmin && (
                  <Link to={dashPath} className="vx-btn vx-btn-ghost vx-nav-full" onClick={closeMenu}>
                    Dashboard
                  </Link>
                )}
                <button
                  type="button"
                  className="vx-btn vx-btn-ghost vx-nav-full"
                  onClick={() => { handleLogout(); closeMenu(); }}
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <>
                <Link to="/login"  className="vx-btn vx-btn-ghost vx-nav-full" onClick={closeMenu}>Connexion</Link>
                <Link to="/signup" className="vx-btn vx-btn-solid vx-nav-full" onClick={closeMenu}>S'inscrire →</Link>
              </>
            )}
          </div>

          <div className="vx-mobile-nav-footer">
            <span>© {new Date().getFullYear()} UltimateXDrive</span>
            <Link to="/search" className="vx-mobile-nav-cta" onClick={closeMenu}>
              Trouver un véhicule →
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
